import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/NewButton';
import { useCaseContext } from '@/components/contexts/CaseContext';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import { useTimezone } from '@/components/contexts/TimezoneContext';
import type { HomeActivity } from '@/hooks/useHomeDashboard';
import { useHomeRecentUploads } from '@/hooks/useHomeRecentUploads';
import { DeadlineWidgetStatus } from '@/pages/home/deadline-widgets/shared';
import { RecentUploadRow } from '@/pages/home/RecentUploadRow';

export function UploadsPanel() {
  const { timezone } = useTimezone();
  const { setSelectedCase } = useCaseContext();
  const { openDocumentPanel } = useSidePanel();
  const {
    items,
    hasMore,
    isLoading,
    isLoadingMore,
    isError,
    loadMore,
    retry,
  } = useHomeRecentUploads({ timezone, enabled: true });

  const openItem = (item: HomeActivity) => {
    if (!item.file_reference_guid) return;
    setSelectedCase(item.matter_guid);
    openDocumentPanel({
      documentGuid: item.file_reference_guid,
      matterGuid: item.matter_guid,
      activeTab: 'document',
    });
  };

  return (
    <Card flat className="flex h-full min-h-0 w-full flex-col px-6">
      <div className="mb-3 py-4">
        <h2 className="text-sm font-semibold tracking-tight text-slate-700">Recent uploads</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Completed documents uploaded in the past month
        </p>
      </div>
      <DeadlineWidgetStatus
        isLoading={isLoading}
        isError={Boolean(isError)}
        onRetry={retry}
        message="Couldn't load recent uploads."
      >
        {items.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            Recent uploads will appear here.
          </p>
        ) : (
          <>
            <ul className="max-h-[min(70vh,40rem)] divide-y divide-slate-100/80 overflow-y-auto">
              {items.map((item) => (
                <RecentUploadRow key={item.id} item={item} onOpen={openItem} />
              ))}
            </ul>
            {hasMore ? (
              <div className="flex justify-center pt-4">
                <Button
                  text="Load more"
                  variant="outline"
                  size="sm"
                  loading={isLoadingMore}
                  onClick={() => void loadMore()}
                />
              </div>
            ) : null}
          </>
        )}
      </DeadlineWidgetStatus>
    </Card>
  );
}
